// src/AlbumDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import ReviewForm from './components/ReviewForm';
import ReviewList from './components/ReviewList';
import ReadOnlyStars from './components/ReadOnlyStars';

export default function AlbumDetail({ currentUser }) {
  const { id } = useParams();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [newReview, setNewReview] = useState(null);
  const [reviewSummary, setReviewSummary] = useState({ avgRating: null, count: 0 });

  // fetch average rating/count for this album
  const fetchSummary = () =>
    fetch(`/api/reviews/summary?type=album&id=${encodeURIComponent(id)}`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(data => setReviewSummary(data))
      .catch(() => setReviewSummary({ avgRating: null, count: 0 }));

  useEffect(() => {
    const ac = new AbortController();

    setLoading(true);
    setError(null);
    setAlbum(null);

    fetch(`/api/spotify/albums/${id}`, { signal: ac.signal })
      .then(async (res) => {
        const text = await res.text(); // handle empty responses safely
        if (!res.ok) {
          const msg =
            res.status === 404
              ? 'Album not found'
              : (text || `Failed to fetch album (${res.status})`);
          throw new Error(msg);
        }
        return text ? JSON.parse(text) : null;
      })
      .then((data) => setAlbum(data))
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err.message || 'Failed to load album');
      })
      .finally(() => setLoading(false));

    return () => ac.abort();
  }, [id]);

  // load summary on mount/id change
  useEffect(() => {
    if (!id) return;
    fetchSummary();
  }, [id]);

  if (loading || (!album && !error)) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading album...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <div className="alert alert-danger">
          <h4>Error loading album</h4>
          <p>{error}</p>
        </div>
      </Container>
    );
  }

  // Safe helpers now that album exists
  const tracks = album?.tracks?.items || [];

  const totalMs = tracks.reduce((sum, t) => sum + (t.duration_ms || 0), 0);

  const formatDuration = (ms) => {
    const hrs = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    if (hrs > 0) return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // Group tracks by disc number and sort both discs and tracks
  const groupTracksByDisc = (list = []) => {
    const map = new Map();
    for (const t of list) {
      const d = t.disc_number || 1;
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(t);
    }
    const discNumbers = [...map.keys()].sort((a, b) => a - b);
    for (const d of discNumbers) {
      map.get(d).sort((a, b) => a.track_number - b.track_number);
    }
    return { map, discNumbers, isMultiDisc: discNumbers.length > 1 };
  };

  const TrackRow = ({ track }) => (
    <Link to={`/track/${track.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card key={track.id} className="hover-shadow">
        <Card.Body>
          <Row className="align-items-center">
            <Col xs="auto" className="track-number">
              {track.track_number}
            </Col>
            <Col className="track-title">{track.name}</Col>
            <Col xs="auto" className="track-duration">
              {Math.floor(track.duration_ms / 60000)}:
              {String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Link>
  );

  const handleReviewSubmitted = (reviewData) => {
    setNewReview(reviewData);
    setReviewSubmitted(true);
    // Refresh the average rating/count right away
    fetchSummary();  
    setTimeout(() => {
      setReviewSubmitted(false);
      setNewReview(null);
    }, 1000);
  };

  return (
    <Container className="my-4">
      {/* Album header — image left, text right */}
      <Row className="justify-content-center">
        <Col xs={12}>
          <div className="song-detail-container">
            {album.images?.[0]?.url && (
              <img
                src={album.images[0].url}
                alt={`${album.name} cover`}
                className="song-detail-image"
              />
            )}

            <div className="song-info">
              <h2 className="song-title">{album.name}</h2>

              {/* Average rating row (like SongDetail) */}
              <div className="d-flex align-items-center gap-2 stars--neutral" style={{ margin: '0.25rem 0 0.75rem' }}>
                {reviewSummary.count > 0 ? (
                  <>
                    <ReadOnlyStars
                      value={reviewSummary.avgRating}
                      ariaLabel={`Average rating ${reviewSummary.avgRating} out of 5`}
                    />
                    <strong style={{ fontSize: '0.95rem' }}>
                      {reviewSummary.avgRating.toFixed(1)}
                    </strong>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      ({reviewSummary.count} review{reviewSummary.count === 1 ? '' : 's'})
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>
                    No reviews yet
                  </span>
                )}
              </div>

              <p className="song-meta">
                {album.artists.map((artist, i) => (
                  <span key={artist.id}>
                    <Link to={`/artist/${artist.id}`}>{artist.name}</Link>
                    {i < album.artists.length - 1 ? ', ' : ''}
                  </span>
                ))}
                {' • '}
                {(album.release_date || '').slice(0, 4) || 'Unknown'}
              </p>

              <p className="song-meta album-duration">{formatDuration(totalMs)}</p>

              <Button
                href={album.external_urls?.spotify}
                target="_blank"
                rel="noopener noreferrer"
                variant="success"
              >
                <i className="bi bi-spotify"></i> Play on Spotify
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Tracklist */}
      <Row className="mt-1">
        <Col xs={12}>
          {(() => {
            const { map, discNumbers, isMultiDisc } = groupTracksByDisc(tracks);

            if (!isMultiDisc) {
              return (
                <div className="tracklist">
                  {tracks
                    .slice()
                    .sort((a, b) => a.track_number - b.track_number)
                    .map((track) => (
                      <TrackRow key={track.id} track={track} />
                    ))}
                </div>
              );
            }

            return discNumbers.map((discNo) => (
              <div key={`disc-${discNo}`} className="mb-4">
                <h4 className="disc-header">Disc {discNo}</h4>
                <div className="tracklist">
                  {map.get(discNo).map((track) => (
                    <TrackRow key={track.id} track={track} />
                  ))}
                </div>
              </div>
            ));
          })()}
        </Col>
      </Row>

      {/* Reviews */}
      <Row className="mt-4">
        <Col>
          <ReviewForm
            spotifyId={id}
            type="album"
            itemName={album.name}
            itemImage={album.images?.[0]?.url}
            onReviewSubmitted={handleReviewSubmitted}
          />
          <ReviewList
            spotifyId={id}
            mode="item"
            onReviewSubmitted={reviewSubmitted}
            showItemInfo={false}
            currentUser={currentUser}
            newReview={newReview}
          />
        </Col>
      </Row>
    </Container>
  );
}
